'use es6';

import PortalIdParser from 'PortalIdParser';
import { getFullUrl } from 'hubspot-url-utils';
import { na1 } from 'hubspot-url-utils/hublets';
import { stringify as stringifyParams } from 'hub-http/helpers/params';
import { parseUrl } from 'hub-http/helpers/url';
import * as CategorySlug from 'ecosystem-marketplace-core/constants/urlSlugs/CategorySlug';
import * as SuperCategorySlug from 'ecosystem-marketplace-core/constants/urlSlugs/SuperCategorySlug';
import { getCategorySuperCategory } from 'marketplace-ui-apps-core/common/utils/groupTags';
import { getLocalizedHubSpotUrl } from 'marketplace-ui-common/utils/urls';
import { getFullHref } from './history';
const APP_ROOT = '/marketplace/apps';
export const getEcosystemOriginUrl = () => getFullUrl('ecosystem');
export const getPublicAppMKPLUrl = () => `${getEcosystemOriginUrl()}/marketplace/apps`;
export const getLoginUrl = () => `${getFullUrl('app', {
  hubletOverride: na1
})}/login`;
export const getAppRootUrl = () => `/ecosystem/${PortalIdParser.get()}${APP_ROOT}`;
export const getAdminPreviewRootUrl = () => '/marketplace';
const getLocation = () => window ? window.location : {};
export const getAppShortlink = (pathname, params = {}) => {
  return `${getFullUrl('app', {
    hubletOverride: na1
  })}/l/ecosystem${APP_ROOT}${pathname}${Object.keys(params).length ? `?${stringifyParams(params)}` : ''}`;
};
export const getLoginUrlWithRedirect = pathname => `${getLoginUrl()}/?loginRedirectUrl=${encodeURIComponent(`${getAppShortlink(pathname)}`)}`;
const getLocalizedUrlPath = (lang = 'en', external = false) => {
  switch (lang) {
    case 'pt':
    case 'pt-br':
      return external ? '/br' : '/pt';
    case 'zh-tw':
    case 'zh':
      return '/zh';
    case 'no-no':
    case 'no':
      return '/no';
    case 'de':
    case 'es':
    case 'fr':
    case 'nl':
    case 'it':
    case 'fi':
    case 'da':
    case 'pl':
    case 'sv':
      return `/${lang}`;
    case 'ja':
      return external ? '/jp' : '/ja';
    default:
      return '';
  }
};
const validLegalLangs = ['ja', 'de', 'en', 'es', 'pt', 'pt-br', 'fr'];
export const getPublicRootUrl = (lang = 'en') => {
  return `${getLocalizedUrlPath(lang)}${APP_ROOT}`;
};
export const getLocalizedPublicPath = (lang, pathname = '') => {
  return `${getPublicRootUrl(lang)}${pathname}`;
};
export const getLocalizedPublicUrl = (lang, pathname) => {
  const {
    origin = getFullUrl('ecosystem', {
      hubletOverride: na1
    })
  } = getLocation();
  return `${origin}${getLocalizedPublicPath(lang, pathname)}`;
};
export const getFormattedUrl = href => {
  if (!href) return null;
  const {
    protocol
  } = parseUrl(href);
  return !protocol ? `http://${href}` : href;
};
export const getSuperCategoryFilterUrl = superCategory => {
  const superCategoryFacet = SuperCategorySlug[superCategory];
  if (!superCategoryFacet) {
    console.error(`Unable to lookup facet for super category '${superCategory}'`);
    return '';
  }
  return `/${superCategoryFacet}`;
};
export const getCategoryFilterUrl = (category, _superCategory) => {
  const categoryFacet = CategorySlug[category];
  if (!categoryFacet) {
    console.error(`Unable to lookup facet for category '${category}'`);
    return '';
  }
  const superCategory = _superCategory || getCategorySuperCategory(category);
  return `${getSuperCategoryFilterUrl(superCategory)}/${categoryFacet}`;
};
export const getListingDetailUrl = (slug, category, superCategory) => {
  return `${getCategoryFilterUrl(category, superCategory)}/${slug}`;
};
const PAGE_PATH_REGEX = /\/page\/\d+/;
export const stripPathPagination = pathname => {
  return pathname.replace(PAGE_PATH_REGEX, '');
};
export const getPathPagination = (pathname, page) => {
  if (page <= 1) {
    return stripPathPagination(pathname);
  }
  const paginationPath = `/page/${page}`;
  const paginationRoot = pathname === '/' ? '' : pathname;
  return PAGE_PATH_REGEX.test(pathname) ? paginationRoot.replace(PAGE_PATH_REGEX, paginationPath) : `${paginationRoot}${paginationPath}`;
};
export const getHasPathPagination = pathname => {
  return PAGE_PATH_REGEX.test(pathname);
};
export const getConnectedAppsUrl = isCustomer => {
  if (!isCustomer) {
    return `${getFullUrl('app', {
      hubletOverride: na1
    })}/l/integrations-settings/installed`;
  }
  return `${getFullUrl('app')}/integrations-settings/${PortalIdParser.get()}/installed`;
};
export const getAllCategoriesUrl = () => '/';
export const getFeaturedAppsUrl = () => '/featured';
export const getFeaturedAppsPublicUrl = lang => `${getPublicRootUrl(lang)}${getFeaturedAppsUrl()}`;
export const getSignupUrl = (lang, pathname) => {
  const url = `${getLocalizedHubSpotUrl(lang)}/products/get-started`;
  if (pathname) {
    const referrer = getFullHref(pathname, false);
    return `${url}?hubs_content=${referrer}`;
  }
  return url;
};
export const getContactUrl = lang => {
  return `${getLocalizedHubSpotUrl(lang, 'offers')}/contact-sales`;
};
export const getAllReviewsUrl = (isPublic, slug, category, superCategory) => {
  const ROOT = isPublic ? getPublicRootUrl() : getAppRootUrl();
  return `${ROOT}${getListingDetailUrl(slug, category, superCategory)}/reviews`;
};
const tierGroupPricingPath = {
  CRM: '/crm',
  MARKETING: '/marketing',
  SALES: '/sales',
  SERVICE: '/service',
  CMS: '/cms',
  OPERATIONS: '/operations',
  OPS: '/operations' // Remove once backend changes plans to use "OPERATIONS_" prefix
};
export const getPricingUrl = (isCustomer, hub) => {
  const path = hub ? tierGroupPricingPath[hub] : '';
  if (!isCustomer) {
    return `https://www.hubspot.com/pricing${path}`;
  }
  return `${window.location.origin}/pricing/${PortalIdParser.get()}${path}`;
};
export const getHubSpotPrivacyUrl = (lang = 'en') => {
  const legalLang = validLegalLangs.includes(lang) ? lang : 'en';
  return `https://legal.hubspot.com${getLocalizedUrlPath(legalLang, true)}/privacy-policy`;
};
export const getHubSpotTermsOfServiceUrl = (lang = 'en') => {
  const legalLang = validLegalLangs.includes(lang) ? lang : 'en';
  return `https://legal.hubspot.com${getLocalizedUrlPath(legalLang, true)}/terms-of-service`;
};
export const getHubSpotAcceptableUseUrl = (lang = 'en') => {
  const legalLang = validLegalLangs.includes(lang) ? lang : 'en';
  return `https://legal.hubspot.com${getLocalizedUrlPath(legalLang, true)}/acceptable-use`;
};
export const getFullSocialImageUrl = (lang, path) => {
  return `${getFullUrl('api', {
    hubletOverride: 'na1'
  })}/ecosystem/public/v1/screenshots/apps/${lang}/marketplace/apps${path.replace(/\/$/, '')}.png`;
};
export const getAppStorefrontRoute = (path = '') => `/${path.replace(/^\/?/, '')}`.replace(/\/$/, '');
export const getCollectionPreviewRoute = collectionId => `/preview/apps/collections/${collectionId}`;